import { datadogLogs } from '@datadog/browser-logs';
import { useSession, useDescope, refresh } from '@descope/react-sdk';
import { useStatsigClient } from '@statsig/react-bindings';
import { useCallback, useEffect } from 'react';
import { useSelectedPharmacy } from '@services/pharmacy';
import {
  isForceEnabled,
  INACTIVITY_CONFIG,
} from './inactivity-detection.constants';
import useInactivityDetection from './use-inactivity-detection';

export function useInactivityLogout() {
  const { isAuthenticated } = useSession();
  const { logEvent } = useStatsigClient();
  const { logout } = useDescope();
  const { clearSelection } = useSelectedPharmacy();

  const { isWarningVisible, timeUntilLogout, isTimedOut, resetTimers } =
    useInactivityDetection({
      enabled: isAuthenticated || isForceEnabled(),
    });

  const handleContinue = useCallback(() => {
    datadogLogs.logger.info('inactivity_session_extended', {
      portal: 'pharmacy',
    });
    logEvent('inactivity_session_extended');

    const now = Date.now();
    localStorage.setItem(INACTIVITY_CONFIG.LAST_ACTIVITY_KEY, now.toString());

    void refresh?.();
    resetTimers();
  }, [logEvent, resetTimers]);

  // Core logout logic without logging (called by both manual and timeout logout)
  const performLogout = useCallback(async () => {
    await logout();

    localStorage.removeItem('DS');
    localStorage.removeItem('DSR');
    localStorage.removeItem(INACTIVITY_CONFIG.LAST_ACTIVITY_KEY);
    clearSelection();

    window.location.reload();
  }, [logout, clearSelection]);

  // Manual logout handler (when user clicks "Log Out" button)
  const handleManualLogout = useCallback(async () => {
    datadogLogs.logger.info('inactivity_manual_logout', { portal: 'pharmacy' });
    logEvent('inactivity_manual_logout');
    await performLogout();
  }, [logEvent, performLogout]);

  useEffect(() => {
    if (isWarningVisible) {
      datadogLogs.logger.info('inactivity_warning_shown', {
        portal: 'pharmacy',
      });
      logEvent('inactivity_warning_shown');
    }
  }, [isWarningVisible, logEvent]);

  useEffect(() => {
    if (isTimedOut) {
      datadogLogs.logger.info('inactivity_session_timeout', {
        portal: 'pharmacy',
      });
      logEvent('inactivity_session_timeout');
      void performLogout();
    }
  }, [isTimedOut, performLogout, logEvent]);

  return {
    isWarningVisible,
    timeUntilLogout,
    handleContinue,
    handleLogout: handleManualLogout,
  };
}
